import {
  Badge,
  Button,
  Dialog,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  helpEmail,
  subscriptionNavigationPath,
} from '$components/subscription-dialog/subscription-dialog';

export interface AuditTrailUpgradeDialogProps {
  isOpen: boolean;
}

export function AuditTrailUpgradeDialog({
  isOpen,
}: AuditTrailUpgradeDialogProps): ReactElement {
  const { t } = useTranslation();

  const onClickBack = () => {
    window.history.back();
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={() => {}} hideCloseButton className="w-max-4xl">
      <div className="flex flex__ai--center m-bottom-xxl">
        <Dialog.Title className="m-bottom-xxs">
          {t('subscription.auditTrailUpgrade.title')}
        </Dialog.Title>
        <Badge
          color="purple"
          size="small"
          bordered
          className="whitespace-nowrap bg-white font-weight-bold"
        >
          {t('subscription.auditTrailUpgrade.badge')}
        </Badge>
      </div>
      <Dialog.Description />

      <div className="flex flex__dir--column gap-xxl font-size-m">
        <p>
          <Trans
            i18nKey="subscription.auditTrailUpgrade.text"
            components={{ bold: <span className="font-weight-bold" /> }}
          />
        </p>

        <div className="flex flex__dir--column gap-l">
          <div className="flex gap-l flex__ai--center">
            <Icon name="generic" size={IconSizeClasses.Small} />
            <p>
              <Trans
                i18nKey="subscription.auditTrailUpgrade.deviceHistory"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
          <div className="flex gap-l flex__ai--center">
            <Icon name="notifications" size={IconSizeClasses.Small} />
            <p>
              <Trans
                i18nKey="subscription.auditTrailUpgrade.notificationAuditing"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
          <div className="flex gap-l flex__ai--center">
            <Icon name="user" size={IconSizeClasses.Small} />
            <p>
              <Trans
                i18nKey="subscription.auditTrailUpgrade.userAccountability"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
        </div>
        <div className="border-solid border-s border-color-purple-500 border-radius-l p-l gap-l bg-gray-50 flex flex__dir--column">
          <div className="flex gap-s">
            <Icon name="info" size={IconSizeClasses.XSmall} className="bg-purple-500" />
            <p>
              <Trans
                i18nKey="subscription.auditTrailUpgrade.gainFullAccess"
                components={{ bold: <span className="font-weight-bold" /> }}
              />
            </p>
          </div>
          <p className="font-size-s color-gray-800 text-align-center">
            <Trans
              i18nKey="subscription.auditTrailUpgrade.helpText"
              components={{
                l: <Link to={`mailto:${helpEmail}`} className="color-blue-500" />,
              }}
            />
          </p>
        </div>
      </div>

      <Dialog.Actions>
        <Dialog.Close>
          <Button variant="secondary" onClick={onClickBack}>
            {t('shared.back')}
          </Button>
        </Dialog.Close>
        <Button variant="primary">
          <Link
            to={subscriptionNavigationPath}
            target="_blank"
            className="flex flex__ai--center"
          >
            {t('subscription.auditTrailUpgrade.button')}
            <Icon
              name="external-link"
              size={IconSizeClasses.XSmall}
              className="m-left-s bg-white"
            />
          </Link>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
